import { useCallback, useEffect, useMemo, useState } from 'react';
import { BadgeDollarSign, Check, Loader, RefreshCw, TrendingUp, X } from 'lucide-react';
import {
  commissionSummary,
  listCommissions,
  listSellers,
  setCommissionStatus,
  type Commission,
  type CommissionSummary,
  type OrgUser,
} from '../api';

/** Panel de comisiones.
 *  🔒 El vendedor recibe SOLO las suyas — lo fuerza el servidor. El selector de
 *  vendedor de abajo solo aparece para el admin, y aunque un vendedor lo
 *  falsificara, el backend no le devolvería comisiones ajenas. */

const MXN = (v: string | number) =>
  `$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

const ESTADOS: Record<Commission['status'], { label: string; cls: string }> = {
  pending: { label: 'Pendiente', cls: 'bg-amber-500/12 text-amber-400 border-amber-500/25' },
  approved: { label: 'Aprobada', cls: 'bg-sky-500/12 text-sky-400 border-sky-500/25' },
  paid: { label: 'Pagada', cls: 'bg-emerald-500/12 text-emerald-400 border-emerald-500/25' },
  cancelled: { label: 'Cancelada', cls: 'bg-red-500/12 text-red-400 border-red-500/25' },
};

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold mt-1 tabular-nums">{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

export function MisComisiones({ isAdmin }: { isAdmin: boolean }) {
  const [items, setItems] = useState<Commission[]>([]);
  const [sum, setSum] = useState<CommissionSummary | null>(null);
  const [sellers, setSellers] = useState<OrgUser[]>([]);
  const [quien, setQuien] = useState<string>('');   // admin: filtrar por vendedor
  const [estado, setEstado] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [cs, s] = await Promise.all([
        listCommissions({ beneficiary: quien || undefined, status: estado || undefined }),
        commissionSummary(quien || undefined),
      ]);
      setItems(cs); setSum(s);
    } catch (e: any) { setErr(e.message); }
    finally { setLoading(false); }
  }, [quien, estado]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isAdmin) return;
    listSellers().then(setSellers).catch(() => setSellers([]));
  }, [isAdmin]);

  const nombreDe = useMemo(() => {
    const m = new Map(sellers.map(s => [s.user_id, s.full_name || s.username]));
    return (id: string) => m.get(id) || id.slice(0, 8);
  }, [sellers]);

  const cambiar = async (c: Commission, st: 'approved' | 'paid' | 'cancelled') => {
    setBusy(c.id);
    try { await setCommissionStatus(c.id, st); await load(); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{isAdmin ? 'Comisiones del equipo' : 'Mis comisiones'}</h2>
          <p className="text-sm text-muted-foreground">
            {isAdmin
              ? 'Todas las comisiones de la organización. Puedes aprobar y marcar como pagadas.'
              : 'Solo tus comisiones. Se generan al cobrarse la venta.'}
          </p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-muted hover:bg-muted/70 transition-colors">
          <RefreshCw size={13} />Actualizar
        </button>
      </div>

      {sum && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Total ganado" value={MXN(sum.total_ganado)} hint="sin contar canceladas" />
          <Stat label="Pendiente" value={MXN(sum.pending)} hint="por aprobar" />
          <Stat label="Aprobada" value={MXN(sum.approved)} hint="por pagar" />
          <Stat label="Pagada" value={MXN(sum.paid)} hint="ya cobrada" />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {isAdmin && (
          <select value={quien} onChange={e => setQuien(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg bg-muted border border-border text-foreground outline-none">
            <option value="">Todo el equipo</option>
            {sellers.map(s => (
              <option key={s.user_id} value={s.user_id}>{s.full_name || s.username}</option>
            ))}
          </select>
        )}
        <select value={estado} onChange={e => setEstado(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg bg-muted border border-border text-foreground outline-none">
          <option value="">Todos los estados</option>
          <option value="pending">Pendientes</option>
          <option value="approved">Aprobadas</option>
          <option value="paid">Pagadas</option>
          <option value="cancelled">Canceladas</option>
        </select>
      </div>

      {err && <div className="rounded-lg border border-red-500/25 bg-red-500/10 text-red-400 px-3 py-2 text-sm">{err}</div>}
      {loading && <p className="text-muted-foreground text-sm flex items-center gap-2"><Loader size={14} className="animate-spin" />Cargando…</p>}

      {!loading && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <BadgeDollarSign size={26} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Aún no hay comisiones.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Se generan solas cuando se <b>cobra</b> una venta con vendedor asignado.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-muted-foreground">
              <tr className="text-left">
                {isAdmin && <th className="px-3 py-2 font-medium">Vendedor</th>}
                <th className="px-3 py-2 font-medium">Venta</th>
                <th className="px-3 py-2 font-medium">Nivel</th>
                <th className="px-3 py-2 font-medium text-right">Base</th>
                <th className="px-3 py-2 font-medium text-right">%</th>
                <th className="px-3 py-2 font-medium text-right">Comisión</th>
                <th className="px-3 py-2 font-medium">Estado</th>
                {isAdmin && <th className="px-3 py-2 font-medium text-right">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id} className="border-t border-border hover:bg-muted/30">
                  {isAdmin && <td className="px-3 py-2">{nombreDe(c.beneficiary_user_id)}</td>}
                  <td className="px-3 py-2 text-muted-foreground">{c.source_type} #{c.source_id}</td>
                  <td className="px-3 py-2">
                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted">
                      N{c.level}{c.level > 1 ? ' · referido' : ''}
                    </span>
                    {!c.auto && <span className="ml-1 text-[10px] text-amber-400">ajuste manual</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{MXN(c.base_amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.rate ? `${Number(c.rate)}%` : '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{MXN(c.amount)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ESTADOS[c.status].cls}`}>
                      {ESTADOS[c.status].label}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {busy === c.id ? <Loader size={13} className="animate-spin inline" /> : (
                        <>
                          {c.status === 'pending' && (
                            <button onClick={() => cambiar(c, 'approved')} title="Aprobar"
                              className="p-1.5 rounded-md bg-sky-500/10 border border-sky-500/25 text-sky-400 hover:bg-sky-500/20 mr-1">
                              <Check size={12} />
                            </button>
                          )}
                          {(c.status === 'pending' || c.status === 'approved') && (
                            <>
                              <button onClick={() => cambiar(c, 'paid')} title="Marcar pagada"
                                className="p-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 hover:bg-emerald-500/20 mr-1">
                                <TrendingUp size={12} />
                              </button>
                              <button onClick={() => cambiar(c, 'cancelled')} title="Cancelar"
                                className="p-1.5 rounded-md bg-red-500/10 border border-red-500/25 text-red-400 hover:bg-red-500/20">
                                <X size={12} />
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
